import React from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAgentOverride } from "@/hooks/useAgentOverride";
import { useAuth } from "@/hooks/useAuth";
import { AgentBanner } from "@/components/shared/AgentBanner";
import { StepIndicator } from "@/components/shared/StepIndicator";
import { useStore } from "@/store";
import { clsx } from "clsx";

const TIER_BADGE: Record<string, string> = {
  Bronze: "text-amber-300 bg-amber-900/40",
  Silver: "text-gray-300 bg-gray-700/40",
  Gold: "text-yellow-300 bg-yellow-900/40",
  Platinum: "text-brand-300 bg-brand-900/40",
};

const NAV_ITEMS = [
  {
    label: "Book Flight",
    paths: [
      "/search",
      "/results",
      "/seats",
      "/passengers",
      "/payment",
      "/confirmation",
    ],
    href: "/search",
    icon: (active: boolean) => (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={active ? 2.2 : 1.8}
        className="w-5 h-5"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M21 3L3 10.5l7 2.5 2.5 7L21 3z" />
        <path d="M10 13l5-5" />
      </svg>
    ),
  },
  {
    label: "My Flights",
    paths: ["/my-flights"],
    href: "/my-flights",
    icon: (active: boolean) => (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={active ? 2.2 : 1.8}
        className="w-5 h-5"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M3 9a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v2a2 2 0 0 0 0 4v2a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-2a2 2 0 0 0 0-4V9z" />
        <path d="M14 7v2M14 11v2M14 15v2" />
      </svg>
    ),
  },
  {
    label: "Profile",
    paths: ["/profile"],
    href: "/profile",
    icon: (active: boolean) => (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={active ? 2.2 : 1.8}
        className="w-5 h-5"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <circle cx="12" cy="8" r="4" />
        <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
      </svg>
    ),
  },
  {
    label: "Contact",
    paths: ["/contact"],
    href: "/contact",
    icon: (active: boolean) => (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={active ? 2.2 : 1.8}
        className="w-5 h-5"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13.1a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 2.39h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 9.73a16 16 0 0 0 6.29 6.29l.92-.92a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
      </svg>
    ),
  },
];

export function DesktopShell() {
  useAgentOverride();
  const { handleSignOut } = useAuth();
  const currentStep = useStore((s) => s.currentStep);
  const isSyncing = useStore((s) => s.isSyncing);
  const isAuthenticated = useStore((s) => s.isAuthenticated);
  const userName = useStore((s) => s.userName);
  const userEmail = useStore((s) => s.userEmail);
  const loyaltyTier = useStore((s) => s.loyaltyTier);
  const milesBalance = useStore((s) => s.milesBalance);
  const photoUrl = useStore((s) => s.photoUrl);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const hideStepIndicator = [
    "/confirmation",
    "/profile",
    "/my-flights",
    "/contact",
    "/admin",
  ].some((p) => pathname === p || pathname.startsWith(p + "/"));
  const isAdmin = useStore((s) => s.isAdmin);
  const tierColor = loyaltyTier
    ? (TIER_BADGE[loyaltyTier] ?? TIER_BADGE.Silver)
    : "";

  const initials = (userName ?? userEmail ?? "U")
    .split(" ")
    .map((n) => n[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <div className="flex min-h-screen bg-app">
      {/* ── Sidebar — brand frame ── */}
      <aside className="w-64 flex flex-col fixed inset-y-0 left-0 z-30 bg-frame">
        {/* Logo — home */}
        <div className="px-6 py-6 border-b border-white/5">
          <button
            onClick={() => navigate("/")}
            aria-label="Pronetx Airlines — Home"
            className="flex flex-col items-start focus-ring rounded"
          >
            <img
              src="https://cdn.prod.website-files.com/69148a80ebab6c41c3621a67/693ba81b001c674edfb758d7_LOGO-PRONETX-Horiz-Black-2-tone-001%202.png"
              alt=""
              width={128}
              height={32}
              className="h-8 w-auto object-contain invert brightness-95"
            />
            <span className="ml-[44px] -mt-0.5 text-[11px] font-bold text-brand-300 tracking-[0.2em] uppercase">
              Airlines
            </span>
          </button>
        </div>

        {/* Nav links */}
        <nav aria-label="Primary" className="flex-1 px-3 py-6 space-y-1">
          {[
            ...NAV_ITEMS,
            ...(isAdmin
              ? [
                  {
                    label: "Admin",
                    paths: ["/admin"],
                    href: "/admin",
                    icon: (active: boolean) => (
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={active ? 2.2 : 1.8}
                        className="w-5 h-5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
                      </svg>
                    ),
                  },
                ]
              : []),
          ].map((item) => {
            const active = item.paths.some((p) => pathname.startsWith(p));
            return (
              <button
                key={item.label}
                onClick={() => navigate(item.href)}
                aria-current={active ? "page" : undefined}
                className={clsx(
                  "w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors duration-150 focus-ring",
                  active
                    ? "bg-brand-500/20 text-brand-200 shadow-sm"
                    : "text-white/65 hover:text-white hover:bg-white/5",
                )}
              >
                {item.icon(active)}
                {item.label}
              </button>
            );
          })}
        </nav>

        <div className="mx-4 h-px bg-white/5" aria-hidden="true" />

        {/* User section */}
        <div className="px-4 pt-5 pb-4 space-y-3">
          {isAuthenticated ? (
            <>
              <div className="flex items-center gap-3">
                <div className="shrink-0 w-9 h-9 rounded-full overflow-hidden ring-2 ring-brand-500/30">
                  {photoUrl ? (
                    <img
                      src={photoUrl}
                      alt=""
                      width={36}
                      height={36}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div
                      className="w-full h-full bg-brand-500/25 flex items-center justify-center"
                      aria-hidden="true"
                    >
                      <span className="text-brand-200 text-xs font-bold">
                        {initials}
                      </span>
                    </div>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-white text-sm font-semibold truncate leading-tight">
                    {userName ?? userEmail ?? "Account"}
                  </p>
                  {userName && userEmail && (
                    <p className="text-white/70 text-xs truncate">
                      {userEmail}
                    </p>
                  )}
                </div>
                {isSyncing && (
                  <span
                    className="text-brand-300 text-xs animate-spin inline-block shrink-0"
                    aria-label="Syncing"
                  >
                    ↻
                  </span>
                )}
              </div>

              {loyaltyTier && (
                <div className="flex items-center gap-2 px-1">
                  <span
                    className={clsx(
                      "text-[11px] font-semibold px-2 py-0.5 rounded-full",
                      tierColor,
                    )}
                  >
                    {loyaltyTier}
                  </span>
                  {milesBalance !== undefined && (
                    <span className="text-[11px] text-white/55 font-medium tabular-nums">
                      {(milesBalance ?? 0).toLocaleString()} mi
                    </span>
                  )}
                </div>
              )}

              <button
                onClick={async () => {
                  await handleSignOut();
                  navigate("/login");
                }}
                className="w-full text-left text-[11px] tracking-[0.01em] text-white/55 hover:text-white transition-colors py-0.5 flex items-center gap-1.5 focus-ring rounded"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={1.8}
                  className="w-3 h-3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" />
                </svg>
                Sign out
              </button>

              <p className="pt-3 border-t border-white/5 text-[9px] text-white/30 tracking-widest uppercase">
                Pronetx © 2026
              </p>
            </>
          ) : (
            <button
              onClick={() => navigate("/login")}
              className="w-full bg-brand-500 hover:bg-brand-600 text-white text-sm font-semibold px-4 py-2.5 rounded-xl transition-colors focus-ring"
            >
              Sign in
            </button>
          )}
        </div>
      </aside>

      {/* ── Main content ── */}
      <div className="ml-64 flex-1 flex flex-col min-h-screen">
        <AgentBanner />
        {!hideStepIndicator && <StepIndicator currentStep={currentStep} />}
        <main className="flex-1 overflow-y-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
